import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import PageHeader, { RefreshButton } from '../components/shell/PageHeader';
import OptionMenu from '../components/tools-catalog/OptionMenu';
import {
    AdminTool, CONTENT_SEGMENTS as KINDS, FieldDef, FIELDS, SEGMENT_LABEL, ToolIcon, byCatalogOrder, moduleOf, segmentState,
} from '../components/tools-catalog/catalogModel';
import '../styles/tools-catalog.scss';

type ViewId = 'all' | 'Active' | 'Debug' | 'Inactive' | 'todo';
const VIEWS: { id: ViewId; label: string }[] = [
    { id: 'all', label: 'Tutti' },
    { id: 'Active', label: 'Attivi' },
    { id: 'Debug', label: 'In debug' },
    { id: 'Inactive', label: 'Inattivi' },
    { id: 'todo', label: 'Contenuti da completare' },
];

const needsContent = (tool: AdminTool) => KINDS.some(({ kind }) => segmentState(tool, kind) !== 'published');
const inView = (tool: AdminTool, view: ViewId) =>
    view === 'all' ? true : view === 'todo' ? needsContent(tool) : tool.status === view;

interface Undo { text: string; revert: () => Promise<void> }

const ToolsListPage: React.FC = () => {
    const { showToast } = useToast();
    const [tools, setTools] = useState<AdminTool[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<ViewId>('all');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);
    const [undo, setUndo] = useState<Undo | null>(null);
    const undoTimer = useRef<number | undefined>(undefined);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setTools((await apiService.adminGetTools<AdminTool>()).sort(byCatalogOrder));
        } catch {
            setError('Impossibile caricare i tool. Verifica i permessi di amministratore.');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { void load(); }, [load]);
    useEffect(() => () => window.clearTimeout(undoTimer.current), []);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return tools.filter(t => inView(t, view) && (!q || `${t.name} ${t.code} ${t.description ?? ''}`.toLowerCase().includes(q)));
    }, [tools, view, search]);

    const selectedVisible = visible.filter(t => selected.includes(t.id));
    const allChecked = visible.length > 0 && selectedVisible.length === visible.length;

    const showUndo = (next: Undo) => {
        window.clearTimeout(undoTimer.current);
        setUndo(next);
        undoTimer.current = window.setTimeout(() => setUndo(null), 8000);
    };

    /** Scrive un valore su uno o più tool: subito a schermo, poi sul server; in caso di errore torna indietro. */
    const save = async (ids: number[], field: FieldDef, value: string, offerUndo = true) => {
        const previous = new Map(tools.filter(t => ids.includes(t.id)).map(t => [t.id, t[field.key]]));
        const apply = (pick: (id: number) => string) =>
            setTools(prev => prev.map(t => (previous.has(t.id) ? { ...t, [field.key]: pick(t.id) } : t)));
        apply(() => value);
        setSaving(true);
        try {
            if (ids.length === 1) await apiService.adminUpdateTool(ids[0], { [field.key]: value });
            else await apiService.adminBulkUpdateTools({ toolIds: ids, [field.key]: value });
        } catch (err) {
            apply(id => previous.get(id)!);
            const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Riprova';
            showToast('Modifica non salvata', message, 'danger');
            return;
        } finally {
            setSaving(false);
        }
        if (!offerUndo) return;
        const label = field.options.find(o => o.value === value)?.label ?? value;
        const name = ids.length === 1 ? tools.find(t => t.id === ids[0])?.name : `${ids.length} tool`;
        showUndo({
            text: `${field.label} di ${name}: ${label}`,
            revert: async () => {
                // Ripristina i valori di prima, raggruppando i tool che avevano lo stesso valore.
                const byValue = new Map<string, number[]>();
                previous.forEach((old, id) => byValue.set(old, [...(byValue.get(old) ?? []), id]));
                for (const [old, group] of byValue) await save(group, field, old, false);
            },
        });
    };

    const toggle = (id: number) =>
        setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    const toggleAll = () => setSelected(allChecked ? [] : visible.map(t => t.id));
    const pickView = (next: ViewId) => { setView(next); setSelected([]); };

    const activeCount = tools.filter(t => t.status === 'Active').length;

    return (
        <div className="cat">
            <PageHeader
                title="Catalogo tool"
                description={loading ? 'Caricamento…' : `${tools.length} tool, ${activeCount} attivi. Cambia stato, visibilità, catalogo e tipo direttamente dalla griglia.`}
                actions={<>
                    <label htmlFor="cat-search" className="visually-hidden">Cerca un tool</label>
                    <input id="cat-search" type="search" className="form-control cat-search" placeholder="Cerca per nome o codice"
                        value={search} onChange={e => setSearch(e.target.value)} />
                    <RefreshButton onClick={load} loading={loading} />
                </>}
            />

            <div className="cat-views" role="group" aria-label="Viste">
                {VIEWS.map(v => {
                    const on = view === v.id;
                    return (
                        <button key={v.id} type="button" className={`cat-view${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => pickView(v.id)}>
                            {v.label}<span className="cat-view__count">{tools.filter(t => inView(t, v.id)).length}</span>
                        </button>
                    );
                })}
            </div>

            {selected.length > 0 && (
                <div className="cat-bulk" role="toolbar" aria-label="Azioni sui tool selezionati">
                    <strong>{selected.length === 1 ? '1 tool selezionato' : `${selected.length} tool selezionati`}</strong>
                    {FIELDS.map(field => (
                        <OptionMenu
                            key={field.key}
                            options={field.options}
                            onPick={value => void save(selected, field, value)}
                            triggerClassName="cat-bulk__btn"
                            ariaLabel={`${field.label} dei tool selezionati`}
                            disabled={saving}
                            trigger={<>{field.label} <i className="bi bi-chevron-down" aria-hidden="true" /></>}
                        />
                    ))}
                    <span className="cat-bulk__spacer" />
                    <button type="button" className="cat-bulk__clear" onClick={() => setSelected([])}>Deseleziona</button>
                </div>
            )}

            {error && <div className="alert alert-danger"><i className="bi bi-exclamation-triangle me-2" />{error}</div>}

            <section className="cat-grid" aria-label="Elenco dei tool">
                <div className="cat-grid__scroll">
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th className="cat-col-check">
                                    <input type="checkbox" className="form-check-input" aria-label="Seleziona tutti i tool visibili"
                                        checked={allChecked} onChange={toggleAll} disabled={!visible.length} />
                                </th>
                                <th>Tool</th>
                                {FIELDS.map(f => <th key={f.key} className={`cat-col-${f.key}`}>{f.label}</th>)}
                                <th className="cat-col-module">Modulo</th>
                                <th className="cat-col-content">Contenuti</th>
                                <th className="cat-col-open"><span className="visually-hidden">Apri</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={9} className="cat-table__message"><span className="spinner-border spinner-border-sm me-2" />Caricamento…</td></tr>
                            )}
                            {!loading && visible.length === 0 && !error && (
                                <tr><td colSpan={9} className="cat-table__message">Nessun tool in questa vista. Cambia vista o cerca un altro nome.</td></tr>
                            )}
                            {!loading && visible.map(tool => (
                                <ToolRow key={tool.id} tool={tool} selected={selected.includes(tool.id)} saving={saving}
                                    onToggle={() => toggle(tool.id)} onChange={(field, value) => void save([tool.id], field, value)} />
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="cat-legend">
                    <span><i className="cat-seg cat-seg--published" aria-hidden="true" />Pubblicato</span>
                    <span><i className="cat-seg cat-seg--draft" aria-hidden="true" />Bozza</span>
                    <span><i className="cat-seg cat-seg--outdated" aria-hidden="true" />Traduzione da aggiornare</span>
                    <span><i className="cat-seg cat-seg--none" aria-hidden="true" />Assente</span>
                    <span className="cat-legend__end">Segmenti: pagina pubblica · scheda · guida</span>
                </div>
            </section>

            {undo && (
                <div className="cat-undo" role="status">
                    <span>{undo.text}</span>
                    <button type="button" onClick={() => { const current = undo; setUndo(null); void current.revert(); }}>Annulla</button>
                </div>
            )}
        </div>
    );
};

const ToolRow: React.FC<{
    tool: AdminTool;
    selected: boolean;
    saving: boolean;
    onToggle: () => void;
    onChange: (field: FieldDef, value: string) => void;
}> = ({ tool, selected, saving, onToggle, onChange }) => {
    const segments = KINDS.map(k => ({ ...k, state: segmentState(tool, k.kind) }));
    const published = segments.filter(s => s.state === 'published').length;
    const drafts = segments.filter(s => s.state === 'draft').length;
    const outdated = segments.some(s => s.state === 'outdated');
    const summary = [`${published} di 3 pubblicati`, drafts ? (drafts === 1 ? '1 bozza' : `${drafts} bozze`) : '', outdated ? 'da aggiornare' : '']
        .filter(Boolean).join(' · ');

    return (
        <tr className={selected ? 'is-selected' : undefined}>
            <td className="cat-col-check">
                <input type="checkbox" className="form-check-input" aria-label={`Seleziona ${tool.name}`} checked={selected} onChange={onToggle} />
            </td>
            <td>
                <div className="cat-tool">
                    <ToolIcon tool={tool} />
                    <div className="cat-tool__text">
                        <Link to={`/tools/${tool.id}`} className="cat-tool__name">{tool.name}</Link>
                        <code className="cat-tool__code">{tool.code}</code>
                    </div>
                    {tool.openReportCount > 0 && (
                        <Link to={`/tools/${tool.id}?tab=qualita`} className="cat-tool__reports">
                            {tool.openReportCount === 1 ? '1 segnalazione' : `${tool.openReportCount} segnalazioni`}
                        </Link>
                    )}
                </div>
            </td>
            {FIELDS.map(field => {
                const current = field.options.find(o => o.value === tool[field.key]) ?? { value: tool[field.key], label: tool[field.key] };
                const tone = (current as { tone?: string }).tone;
                const extra = field.key === 'visibility' && tool.visibility === 'Restricted' ? ` (${tool.assignedUserCount})` : '';
                return (
                    <td key={field.key} className={`cat-col-${field.key}`}>
                        <OptionMenu
                            options={field.options}
                            value={tool[field.key]}
                            onPick={value => onChange(field, value)}
                            triggerClassName="cat-cell"
                            ariaLabel={`${field.label} di ${tool.name}: ${current.label}. Cambia`}
                            disabled={saving}
                            trigger={<>
                                <span className={field.pill ? `cat-pill cat-pill--${tone ?? 'muted'}` : 'cat-plain'}>
                                    {current.dot && field.key !== 'toolType' && <span className="cat-dot" style={{ background: current.dot }} aria-hidden="true" />}
                                    {current.label}{extra}
                                </span>
                                <i className="bi bi-chevron-down cat-cell__caret" aria-hidden="true" />
                            </>}
                        />
                    </td>
                );
            })}
            <td className="cat-col-module">
                <span className="cat-module" title="Si ricava dal codice: i tool custom iniziano con custom-">
                    <i className="bi bi-lock" aria-hidden="true" />{moduleOf(tool.code)}
                </span>
            </td>
            <td className="cat-col-content">
                <Link to={`/tools/${tool.id}?tab=contenuti`} className="cat-content"
                    aria-label={`Contenuti di ${tool.name}: ${segments.map(s => `${s.label} ${SEGMENT_LABEL[s.state]}`).join(', ')}`}>
                    <span className="cat-content__bars">
                        {segments.map(s => <i key={s.kind} className={`cat-seg cat-seg--${s.state}`} title={`${s.label}: ${SEGMENT_LABEL[s.state]}`} />)}
                    </span>
                    <span className={`cat-content__text${outdated ? ' is-outdated' : drafts ? ' is-draft' : ''}`}>{summary}</span>
                </Link>
            </td>
            <td className="cat-col-open">
                <Link to={`/tools/${tool.id}`} className="cat-open" aria-label={`Apri ${tool.name}`}><i className="bi bi-chevron-right" aria-hidden="true" /></Link>
            </td>
        </tr>
    );
};

export default ToolsListPage;
